/// <amd-module name="Repos/Editor/Components/CodeEditor" />
import * as React from "react";
import { IFileViewerProps } from "Repos/Common/Contracts/Editor";
import { detectBidiCharacterInLine } from "Repos/Common/Util/BidiDetector";
import * as Resources from "Repos/Editor/Resources";
import { ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { Editor } from "VSS/Features/Monaco/Components/Editor";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Observer } from "VSSUI/Observer";
import { getSearchContextMenuItems } from "../SearchContextMenuItemExtension";
import { getEditorUserPreferencesService } from "../UserPreferencesService";
import { unregisterDefaultKeyBindings } from "./Shortcuts";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssThemeService } from "VSS/Platform/Theming";

export interface ICodeEditorProps extends IFileViewerProps {
    /**
     * Callback to receive a reference to the monaco editor object
     */
    editorRef?: (editor: monaco.editor.ICodeEditor | null) => void;

    /**
     * If true, force single-line mode (useful for situations like Blame)
     */
    forceSingleLineMode?: boolean;
}

export class CodeEditor extends VssComponent<ICodeEditorProps> {
    private editorRef: monaco.editor.ICodeEditor;
    private editable: boolean;
    private truncatedMessageDismissed = new ObservableValue(false);
    private bidiMessageDismissed = new ObservableValue(false);

    constructor(props: ICodeEditorProps, context: IVssComponentContext) {
        super(props, context);

        this.editable = ObservableLike.getValue(props.editable || false);

        const preferences = getEditorUserPreferencesService(context.pageContext).getPreferences();
        preferences.subscribe(() => this.editorRef && this.editorRef.updateOptions(this.getEditorOptions()));
    }

    public componentDidMount() {
        super.componentDidMount();

        this.observe([
            {
                propertyName: "editable",
                observer: value => {
                    if (this.editable !== value) {
                        this.editable = value;
                        if (this.editorRef) {
                            this.editorRef.updateOptions({ readOnly: !value });
                            if (value) {
                                // Focus the editor when entering edit mode
                                this.editorRef.focus();
                            }
                        }
                    }
                }
            },
            {
                propertyName: "scrollToLine",
                observer: value => {
                    const newPosition = { column: 0, lineNumber: value || 1 };
                    this.editorRef.setPosition(newPosition);
                    this.editorRef.revealPositionInCenter(newPosition);
                }
            }
        ]);
    }

    public render() {
        const { forceSingleLineMode, showMarginForIcons } = this.props;

        let truncatedMessage: JSX.Element | null = null;
        if (this.props.content.contentTruncated) {
            truncatedMessage = (
                <Observer dismissed={this.truncatedMessageDismissed}>
                    {(props: { dismissed: boolean }) => {
                        return (
                            !props.dismissed && (
                                <MessageBar
                                    className="flex-noshrink"
                                    onDismiss={() => (this.truncatedMessageDismissed.value = true)}
                                    severity={MessageBarSeverity.Warning}
                                >
                                    <FormatComponent format={Resources.ContentTruncated}>
                                        <Link href={this.props.getDownloadUrl && this.props.getDownloadUrl(true)}>{Resources.Download}</Link>
                                    </FormatComponent>
                                </MessageBar>
                            )
                        );
                    }}
                </Observer>
            );
        }

        let bidiCharactersMessage: JSX.Element | null = null;
        if (detectBidiCharacterInLine(this.props.content.content)) {
            bidiCharactersMessage = (
                <Observer dismissed={this.bidiMessageDismissed}>
                    {(props: { dismissed: boolean }) => {
                        return (
                            !props.dismissed && (
                                <MessageBar
                                    className={"flex-noshrink"}
                                    severity={MessageBarSeverity.Warning}
                                    onDismiss={() => (this.bidiMessageDismissed.value = true)}
                                >
                                    <FormatComponent format={Resources.BidiWarningMessage}>
                                        <Link href={Resources.BidiDocsLink} target="_blank">{Resources.BidiWarningLinkMessage}</Link>
                                    </FormatComponent>
                                </MessageBar>
                            )
                        );
                    }}
                </Observer>
            );
        }

        const editor = (
            <Editor
                actions={this.getEditorActions()}
                className={css("repos-file-editor flex-grow scroll-hidden", showMarginForIcons && "repos-file-editor-with-margin-icon")}
                content={{
                    content: this.props.content.content,
                    contentUri: this.props.content.resourceUri
                }}
                editorOptions={this.getEditorOptions()}
                editorRef={this.onEditorRef}
                fileExtension={this.props.content.metadata.extension}
                fileUrl={this.props.content.resourceUri}
                getSelectedTextLink={this.props.getCodeSelectionLinkUrl}
                onContentUpdated={this.onContentUpdated}
                widgets={this.props.editorWidgets}
                delayedHighlighting={this.delayedHighlighting}
            />
        );

        if (truncatedMessage || bidiCharactersMessage) {
            return (
                <div className="flex-column flex-grow">
                    {truncatedMessage}
                    {bidiCharactersMessage}
                    <div className="flex-row flex-grow">{editor}</div>
                </div>
            );
        } else {
            return editor;
        }
    }
    
    private decorations: string[] = [];

    private getEditorActions() {
        const actions: monaco.editor.IActionDescriptor[] = getSearchContextMenuItems(this.context.pageContext);
        actions.push(getEditorUserPreferencesService(this.context.pageContext).getAction(false));
        return actions;
    }

    private getEditorOptions() {
        const { forceSingleLineMode, showMarginForIcons } = this.props;
        return getEditorUserPreferencesService(this.context.pageContext).getEditorOptions(this.editable, forceSingleLineMode, showMarginForIcons);
    }

    private onEditorRef = (ref: monaco.editor.ICodeEditor) => {
        this.editorRef = ref;
        if (this.props.editorRef) {
            this.props.editorRef(ref);
        }

        // Disable every Ctrl-K chord shortcut as they would prevent our standard Ctrl-K to add a link.
        unregisterDefaultKeyBindings(this.editorRef, "ctrl+K");

        this.props.onLoaded && this.props.onLoaded();

        if (this.props.initialSelectedText) {
            if (isFeatureFlagEnabled(this.context.pageContext, "Git.PullRequest.MonacoLinkToCodeSelection")) {
                const themeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
                const theme = themeService.getCurrentTheme().isDark ? "vs-dark" : "vs";
                const themedClassName = `${theme}-highlight`
                if (this.props.initialSelectedText) {
                    this.decorations = this.editorRef.deltaDecorations([], [
                        {
                            range: this.props.initialSelectedText,
                            options: { className: themedClassName },
                            
                        }
                    ]);
                    this.editorRef.revealRangeInCenterIfOutsideViewport(this.props.initialSelectedText);
                    this.editorRef.onMouseDown(() => { 
                        this.decorations = this.editorRef.deltaDecorations(this.decorations, []);
                    });
                }
            }
            else {
                this.editorRef.setSelection(this.props.initialSelectedText);
                this.editorRef.revealRangeInCenterIfOutsideViewport(this.props.initialSelectedText);
            }
        } else if (this.props.initialScrollPosition) {
            this.editorRef.setScrollPosition(this.props.initialScrollPosition);
        }

        if (this.props.editable && ObservableLike.getValue(this.props.editable)) {
            this.editorRef.focus();
        }

        if (this.props.onScrollChanged) {
            this.editorRef.onDidScrollChange(event => {
                if (event.scrollTopChanged) {
                    this.props.onScrollChanged!({ scrollLeft: event.scrollLeft, scrollTop: event.scrollTop });
                }
            });
        }
    };

    /**
     * Provide highlighting when a monaco range is being passed into the editor
     * @param selectedRanges
     * @param theme 
     */
    private delayedHighlighting = (selectedRanges: monaco.IRange[], theme: string) => {
        const themedClassName = `${theme}-highlight`;
        let highlightDecorationId: string[] = [];

        // Create a map of the monaco ranges with the startline number as the key of the map
        // This helps finding all the associated highlighting ranges on every mouse move
        const locationRangesMap = new Map<number, monaco.Range[]>();
        selectedRanges.forEach(range => {
            const ranges: monaco.Range[] = locationRangesMap.get(range.startLineNumber) || [];
            ranges.push(new monaco.Range(range.startLineNumber, range.startColumn, range.endLineNumber, range.endColumn));
            locationRangesMap.set(range.startLineNumber, ranges);
        });

        // Helper method to highlight with the specified colour on the given range
        const setHighlightRange = (range?: monaco.Range | null) => {
            if (!range) {
                highlightDecorationId = this.editorRef.deltaDecorations(highlightDecorationId, []);
                return;
            }

            highlightDecorationId = this.editorRef.deltaDecorations(highlightDecorationId, [{
                range: range,
                options: {
                    className: themedClassName
                }
            }]);
        }

        const handleMouseMove = (e: monaco.editor.IEditorMouseEvent) => {
            const eventRange = e.target.range;
            if (eventRange) {
                const ranges = locationRangesMap.get(eventRange.startLineNumber) ?? [];
                for (const range of ranges) {
                    if (range.intersectRanges(eventRange)) {
                        return setHighlightRange(range);
                    }
                }
            }
            setHighlightRange(null);
        };

        this.editorRef.onMouseMove(handleMouseMove);
        this.editorRef.onMouseLeave(e => setHighlightRange(null));
        this.editorRef.onMouseDown(e => {
            setHighlightRange(null);
            if (isFeatureFlagEnabled(this.context.pageContext, "Git.PullRequest.MonacoLinkToCodeSelection")) {
                this.decorations = this.editorRef.deltaDecorations(this.decorations, []);
            }
        })

        this.editorRef.onMouseUp(e => setHighlightRange(null));
    };

    private onContentUpdated = (editor: monaco.editor.ICodeEditor) => {
        if (this.props.onContentChanged) {
            const model = editor.getModel() as monaco.editor.ITextModel;
            this.props.onContentChanged(model.getValue());
        }
    };
}
